import { ButtonType, ButtonWidget, CompactInlineAlertWidget, InlineAlertType, InlineAlertWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Messages } from '../../codegen/Localize';
import { angularProviderHOC } from '../../common/react/AngularProviderHOC';
import { InterpolatedString } from '../../common/react/components/InterpolatedString';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { L10n } from '../../ts/L10n/L10n';
import { IMetricInfo } from '../models/IMetricInfo';
import { IUserInfo } from '../../ts/models/IUserInfo';
import { resumeMetricFromExplorer } from '../actions/UpdateMetricsActions';
import { ServiceLocator } from '../../app/ServiceLocator';
import { MapDispatchToProps, MapStateToProps } from '../../app/utils/ConnectUtils';
import { currentUserIsOwnerOrAdmin } from '../../app/utils/MixedContentHelpers';
import { DetailsTooltipStyles } from '../../app/spaces/CardView/DetailsTooltipStyles';
import { MarkupField } from '../../app/spaces/Components/MarkupField';
import { Name } from '../../app/spaces/Tooltips/Elements/Name';
import { RelatedEntity } from '../../app/spaces/Tooltips/Elements/RelatedEntity';
import { Tags } from '../../app/spaces/Tooltips/Elements/Tags';
import { Timestamp } from '../../app/spaces/Tooltips/Elements/Timestamp';
import { Tooltip } from '../../app/spaces/Tooltips/Elements/Tooltip';
import { User } from '../../app/spaces/Tooltips/Elements/User';
import { ContentTooltipProps, getProjectLink, TooltipDescriptionTestId } from '../../app/spaces/Tooltips/TooltipHelper';
import { HelpService } from '../../commonInfra/services/HelpService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { EmbeddableMetricDataQualityWarning } from '@tableau/data-catalog-client-components';
import { DataCatalogUIActionsService } from '../../ts/services/DataCatalogUIActionsService';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { selectCatalogingOnSiteEnabled } from '../../catalog/selectors/CatalogSelectors';
import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { LocationField } from '~src/app/spaces/Tooltips/Elements/LocationField';

interface MetricTooltipStateProps {
  userIsOwnerOrAdmin: boolean;
  catalogingOnSiteEnabled: boolean;
}

interface MetricTooltipDispatchProps {
  resumeMetricFromExplorer: (metricId: string) => void;
}

interface MetricTooltipAngularProps {
  translate: IReactTranslateService;
  resumeMetricHelpLink: string;
}

export type MetricTooltipWithoutAngularProps = MetricTooltipStateProps & MetricTooltipDispatchProps & ContentTooltipProps;
export type MetricTooltipProps = MetricTooltipWithoutAngularProps & MetricTooltipAngularProps;

export function MetricTooltip(props: MetricTooltipProps): JSX.Element {
  const { urls } = props;
  const content = props.content as IMetricInfo;

  const owner: IUserInfo = content.owner;

  // workaround for the incompleteness of the original metric content type.
  const description = content.description;
  const tags = content.tags;

  /* Just commenting this out in case we wanted to quickly add connectedView's back in.
  const connectedView = content.connectedView;

  let connectedViewLink: EntityLinkProps;
  if (connectedView) {
    const viewName = connectedView.isViewModified ? connectedView.name + '*' : connectedView.name;
    connectedViewLink = {
      href: urls.viewUrl(connectedView.path),
      iconImagePath: null,
      name: viewName
    };
  } else {
    connectedViewLink = {
      name: Messages.unavailable(),
      textStyleOverride: GreyedOutTextStyle,
      href: '',
      iconImagePath: null
    };
  }
  */

  const projectLink = getProjectLink(content.project, urls);
  const locationType = content.location?.type;

  return (
    <Tooltip>
      <Name name={content.name} />
      {content.suspended && <MetricSuspendedWarning {...props} />}
      {renderDataQualityWarnings(props)}
      <MarkupField markup={description}
                   testId={TooltipDescriptionTestId}
                   extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]} />
      <Tags tags={tags} />
      {/*<RelatedEntity label={Messages.metricConnectedView()} link={connectedViewLink}/>*/}
      {!FeatureFlags.personalSpaceEnabled && !props.inProjectSpace && <RelatedEntity label={Messages.project()} link={projectLink} /> }
      {FeatureFlags.personalSpaceEnabled && <LocationField inProjectSpace={props.inProjectSpace} inPersonalSpace={props.inPersonalSpace} locationType={locationType} projectLink={projectLink}/>}
      <User user={owner} label={Messages.owner()} urls={urls} />
      <CompactInlineAlertWidget
        type={InlineAlertType.Info}
        testId='metric-authentication-info-alert'
        style={{marginBottom: '18px'}}
        children={
          <>{Messages.metricsAuthenticationInfo()}
            {' '}
            <TextLinkWidget href={HelpService.getServerHelpUrlByCategory('metrics-data')} testId="link">{Messages.metricsAuthenticationInfoLinkText()}</TextLinkWidget>
          </>
        }
        />

      {/* "Refreshed": Last time the metric data store gets updated
          "Modified": last time the metric itself was modified */}
      {props.lastUpdatedTimestamp && <Timestamp label={Messages.metricLastUpdated()} value={props.lastUpdatedTimestamp} />}
    </Tooltip>
  );
}

function renderDataQualityWarnings(props: MetricTooltipProps): JSX.Element {
  const content = props.content as IMetricInfo;
  if (FeatureFlags.metricsDataQualityWarningsEnabled && props.catalogingOnSiteEnabled) {
    return (
      <div style={{marginBottom: '18px'}}>
        <EmbeddableMetricDataQualityWarning
          apiRequests={CatalogApiRequests}
          uiActions={DataCatalogUIActionsService.instance()}
          siteUrls={props.urls} featureFlags={catalogFeatureFlagsAdapter()}
          client={catalogApolloClient} metricLuid={content.luid} side={'left'}
        />
      </div>
    );
  }
}

function MetricSuspendedWarning(props: MetricTooltipProps): JSX.Element {
  const inlineAlertProps = {
    testId: 'metrics-suspended',
    type: InlineAlertType.Warning,
    style: { marginBottom: '18px' }
  };

  if (props.userIsOwnerOrAdmin) {
    const resumeButton = (
      <ButtonWidget
        buttonType={ButtonType.Outline}
        testId="metrics-resume"
        onClick={() => props.resumeMetricFromExplorer(props.content.id)}
      >
        {Messages.metricResumeButtonLabel()}
      </ButtonWidget>
    );

    return (
      <InlineAlertWidget {...inlineAlertProps} button={resumeButton}>
        {renderSuspendedWarningWithResumeContent(props)}
      </InlineAlertWidget>
    );

  } else {
    return (
      <InlineAlertWidget {...inlineAlertProps}>
        {Messages.metricSuspendedNoResumeTooltip()}
      </InlineAlertWidget>
    );
  }
}

function renderSuspendedWarningWithResumeContent(props: MetricTooltipProps): JSX.Element {
  const learnMoreLink = (
    <TextLinkWidget
      href={props.resumeMetricHelpLink}
      target="_blank"
      testId="metrics-resume-help"
    >
      {Messages.learnMore()}
    </TextLinkWidget>
  );

  return (
    <InterpolatedString
      baseStringKey="metricSuspendedWithResumeTooltip"
      translate={props.translate}
      replacements={{
        'SUSPEND_DATE': () => <span>{L10n.localizedDate(props.lastUpdatedTimestamp)}</span>,
        'LEARN_MORE_LINK': () => learnMoreLink,
      }}
    />
  );
}

interface MetricTooltipAngularServices {
  translateService: ng.translate.ITranslateService;
}

const MetricTooltipAngular = angularProviderHOC<MetricTooltipWithoutAngularProps, MetricTooltipAngularProps, MetricTooltipAngularServices>({
  getAngularServicesWithServiceLocator: () => ({
    translateService: ServiceLocator.get<ng.translate.ITranslateService>('$translate'),
  }),
  selectPropsFromAngularServices: (angularServices) => ({
    translate: angularServices.translateService.instant,
    resumeMetricHelpLink: HelpService.getServerHelpUrlByCategory('metrics-suspend'),
  })
})(MetricTooltip);

const mapStateToProps: MapStateToProps<MetricTooltipStateProps, ContentTooltipProps> = (state, ownProps) => ({
  userIsOwnerOrAdmin: currentUserIsOwnerOrAdmin(state, ownProps.content),
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state)
});

const mapDispatchToProps: MapDispatchToProps<MetricTooltipDispatchProps> = (dispatch) => bindActionCreators({
  resumeMetricFromExplorer
}, dispatch);

export const ConnectedMetricTooltip = connect(mapStateToProps, mapDispatchToProps)(MetricTooltipAngular);
